import * as Ariakit from '@ariakit/react';
import type { ReactNode } from 'react';
import {
  useMemo,
  useRef,
  useState,
  useTransition,
  useEffect,
  Children,
  isValidElement,
} from 'react';
import { matchSorter } from 'match-sorter';
import { styled } from 'styled-components';
import { isArray, isString } from '@wistia/type-guards';
import { Tag } from '../Tag';
import { Icon } from '../Icon';
import { inputCss } from '../../css/inputCss';
import { getTypographicStyles } from '../../private/helpers/getTypographicStyles/getTypographicStyles';

const ComboboxWrapper = styled.div<{ $fullWidth: boolean }>`
  ${inputCss};
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  padding: var(--wui-input-vertical-padding) var(--wui-input-horizontal-padding);
  background-color: var(--wui-input-color-bg);
  border: none;
  outline: 1px solid var(--wui-input-color-border);
  outline-offset: -1px;
  border-radius: var(--wui-input-border-radius);
  font-size: var(--wui-input-font-size);
  line-height: var(--wui-input-line-height);
  font-weight: var(--wui-input-font-weight);
  color: var(--wui-input-color-text);
  display: inline-flex;
  flex-wrap: wrap;
  gap: var(--wui-space-01);

  > * {
    width: min-content !important;
  }

  &:focus-within {
    outline: 2px solid var(--wui-input-color-border-focus);
    outline-offset: -2px;
  }

  input {
    outline: 0 none;
    border: 0 none;

    &::placeholder {
      color: var(--wui-input-placeholder);
      opacity: 1;
    }

    &:focus {
      outline: 0;
    }

    &[aria-invalid='true'] {
      outline-color: var(--wui-input-color-border-error);
    }

    &:disabled {
      outline-color: var(--wui-color-border-disabled);
      background-color: var(--wui-color-bg-surface-disabled);
    }
  }
`;

const ComboboxInput = styled(Ariakit.Combobox)`
  appearance: none;
  padding: 0;
  width: 100%;
  border: 0 none;
  flex: 1;

  &[data-focus-visible] {
    outline-style: solid;
  }

  &[data-active-item] {
    outline-width: 2px;
  }
`;

const ComboboxPopover = styled(Ariakit.ComboboxPopover)`
  --wui-combobox-content-border: var(--wui-color-border);
  --wui-combobox-content-bg: var(--wui-color-bg-surface);
  --wui-combobox-content-border-radius: var(--wui-border-radius-02);
  --wui-combobox-option-padding: var(--wui-space-02);
  --wui-combobox-option-inner-gap: var(--wui-space-03);
  --wui-combobox-option-bg-hover: var(--wui-color-bg-surface-hover);

  border-radius: var(--wui-border-radius-02);
  padding: var(--wui-space-02);
  max-height: var(--wui-popover-max-height, 300px);
  background-color: var(--wui-color-bg-surface);
  box-shadow: var(--wui-elevation-01);
  border: 1px solid var(--wui-color-border);
  position: relative;
  z-index: 50;
  display: flex;
  flex-direction: column;
  overflow: auto;
  overscroll-behavior: contain;
  margin-top: var(--wui-space-02);

  &[aria-busy='true'] {
    --item-opacity: 0.5;
  }
`;

export const ComboboxItem = styled(Ariakit.ComboboxItem)`
  ${getTypographicStyles('body3')};
  display: flex;
  padding: var(--wui-combobox-option-padding);
  gap: var(--wui-combobox-option-inner-gap);
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  border-radius: var(--wui-border-radius-01);
  align-items: center;
  cursor: pointer;
  justify-content: space-between;

  &:hover,
  &:focus-visible,
  &[data-focus-visible='true'],
  &[data-active-item='true'] {
    background-color: var(--wui-combobox-option-bg-hover);
    outline: none;
  }

  &[aria-disabled='true'] {
    color: var(--wui-color-text-disabled);
  }

  &[aria-disabled='true']:hover {
    background-color: transparent;
  }
`;

const NoResults = styled.div`
  gap: var(--wui-space-02);
`;

const POPOVER_WIDTH_OFFSET = 20;

const extractOptions = (children: ReactNode) => {
  const options: Record<string, ReactNode> = {};
  Children.forEach(children, (child) => {
    if (
      isValidElement<{ value: string; children?: ReactNode }>(child) &&
      'value' in child.props &&
      'children' in child.props
    ) {
      options[child.props.value] = child.props.children ?? child.props.value;
    }
  });
  return options;
};

const defaultDisplayValues = (values: string[] | string, onRemove: (value: string) => void) => {
  const valueArray = Array.isArray(values) ? values : [values];
  return valueArray.map((selectedValue) => (
    <Tag
      key={selectedValue}
      label={selectedValue}
      onClickRemove={() => onRemove(selectedValue)}
      onClickRemoveLabel={`Remove ${selectedValue}`}
    />
  ));
};

export type ComboboxProps = {
  /**
   * If true, the popover will flip to stay within the viewport
   */
  flipPopover?: Ariakit.ComboboxPopoverProps['flip'];
  /**
   * If true, the combox will fill the width of its parent
   */
  fullWidth?: boolean;
  /**
   * The options to display in the combobox
   */
  children: ReactNode;

  /**
   * Function to render the selected values. Receives the selected values and a function to remove a value. This is only applicable for multi-select comboboxes.
   */
  displayValues?: (values: string[] | string, onRemove: (value: string) => void) => ReactNode;

  /**
   * The placeholder text for the combobox input
   */
  placeholder?: string;
  /**
   * The function to call when the search value changes.
   */
  onSearchValueChange: (value: string) => void;
  /**
   * The search value of the combobox input
   */
  searchValue: string;
  /**
   * Callback that fires when a value is selected
   */
  onChange: (value: string[] | string) => void;
  /**
   * The selected values of the combobox
   */
  value: string[] | string;
};

export const Combobox = ({
  placeholder,
  value = [],
  onChange,
  searchValue,
  onSearchValueChange,
  displayValues = defaultDisplayValues,
  children,
  flipPopover = true,
  fullWidth = true,
}: ComboboxProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [wrapperWidth, setWrapperWidth] = useState('auto');
  const comboboxWrapperRef = useRef<HTMLDivElement | null>(null);

  const options = useMemo(() => extractOptions(children), [children]);

  const isMultiSelect = isArray(value);

  useEffect(() => {
    const comboboxWrapper = comboboxWrapperRef.current;
    if (comboboxWrapper) {
      const updateWidthVariable = () => {
        const { width } = comboboxWrapper.getBoundingClientRect();
        setWrapperWidth(`${width - POPOVER_WIDTH_OFFSET}px`);
      };

      updateWidthVariable();
      window.addEventListener('resize', updateWidthVariable);

      return () => {
        window.removeEventListener('resize', updateWidthVariable);
      };
    }

    return () => null;
  }, []);

  const handleRemoveValue = (valueToRemove: string) => {
    if (isString(value)) {
      onChange('');
      return;
    }
    onChange(isString(value) ? '' : value.filter((val: string) => val !== valueToRemove));
  };

  const matches = useMemo(() => {
    return matchSorter(Object.keys(options), searchValue);
  }, [options, searchValue]);

  return (
    <Ariakit.ComboboxProvider
      open={isOpen}
      selectedValue={value}
      setOpen={setIsOpen}
      setSelectedValue={onChange}
      setValue={(nextValue) => {
        startTransition(() => {
          onSearchValueChange(nextValue);
        });
      }}
      value={!isOpen && !isMultiSelect ? value : searchValue}
    >
      <ComboboxWrapper
        ref={comboboxWrapperRef}
        $fullWidth={fullWidth}
      >
        {isMultiSelect ? displayValues(value, handleRemoveValue) : null}
        <ComboboxInput
          autoSelect={true}
          placeholder={placeholder}
        />
      </ComboboxWrapper>
      <ComboboxPopover
        aria-busy={isPending}
        flip={flipPopover}
        gutter={8}
        onClose={() => onSearchValueChange('')}
        sameWidth={false}
        style={{
          width: wrapperWidth,
        }}
      >
        {matches.map((match) => (
          <ComboboxItem
            key={match}
            className="combobox-item"
            value={match}
          >
            {options[match]}
            <Icon
              size="md"
              style={{
                opacity: value.includes(match) ? 1 : 0,
              }}
              type="checkmark"
            />
          </ComboboxItem>
        ))}
        {!matches.length && <NoResults>No results found</NoResults>}
      </ComboboxPopover>
    </Ariakit.ComboboxProvider>
  );
};
